import {aecModelDataToLevels} from '../levels/LevelUtils.js';
import DropMeExtension from './DropMe.js';

const av = Autodesk.Viewing;
const avp = av.Private;

const ContextMenuCallbackName = 'ViewObjectIn3D';

/*
//   This file contains the logic of choosing the right position to watch a selected object in the 3D scene.
//   The logic of choosing the right position is created in steps:
//   1) Creating 8 different positions around the selected object.
//   2) Check if there are intersections between the optional positions to the selected object.
//      Choose the ones which have the least intersections, and ignore positions which looks on very narrow sides of the object
//      (like in doors and walls).
//   3) From the chosen positions, check more intersections between the position and different points on the object.
//      Choose the one with the least intersections. If there are more then one with the same - choose the closest.
//   If this logic is happening after the geometry was already loaded, we check intersections with raycast and actual geometries.
//   If it happens before geometry was loaded we will wait for the bounding-boxes and check intersections with them.
//
//   While waiting for the geometry to load, there is an interval that is eliminating objects that are hiding the object during the
//   waiting time.
*/

export default class ViewObjectIn3D {

    constructor(dropMeExt, viewer, options) {

        this.viewer = viewer;
        this.dropMeExt = dropMeExt;
        this.options = options;
        this._onModelAdded = this._onModelAdded.bind(this);
        this._onGeometryLoaded = this._onGeometryLoaded.bind(this);
        this._onModelUnloaded = this._onModelUnloaded.bind(this);
        this._onAggregatedSelectionChanged = this._onAggregatedSelectionChanged.bind(this);
    }

    activate() {
        this.viewer.registerContextMenuCallback(ContextMenuCallbackName, (menu) => {
            const is2d = this.viewer.model.is2d();

            // Don't support manually aligned viewports, since we won't have the required object DB link anyway.
            var isSupported = this.options.enableViewIn3d && this.dropMeExt.isSupportedModel(this.viewer.model, false, true);
            var selected = isSupported && this.viewer.getAggregateSelection();
            if (is2d && selected && selected.length === 1 && selected[0].selection.length === 1) {
                this.pos3dFrom2d = this._getSelected3dPosFrom2d(selected[0].selection[0], selected[0].model);
                menu.push({
                    title: "View object in 3D",
                    target: () => this.selectObject(selected[0].selection[0]),
                });
            }
        });
    }

    deactivate() {
        this.viewer.unregisterContextMenuCallback(ContextMenuCallbackName);
    }

    selectObject(selectedObjectID) {
        this.viewer.addEventListener(av.MODEL_ADDED_EVENT, this._onModelAdded, { once: true });
        this.viewer.addEventListener(av.GEOMETRY_LOADED_EVENT, this._onGeometryLoaded);
        this.viewer.addEventListener(av.MODEL_UNLOADED_EVENT, this._onModelUnloaded);
        this.viewer.addEventListener(av.AGGREGATE_SELECTION_CHANGED_EVENT, this._onAggregatedSelectionChanged);
        this.selectedObjectID = selectedObjectID;
        this.dropMeExt.dispatchEvent({ type: DropMeExtension.OBJECT_SELECTED_EVENT, selectedObjectID});
        if (this.options.onDrop) {
            const sheetNode = this.viewer.model.getDocumentNode();
            const main3DNode = this.options.getMain3DView?.(sheetNode);
            this.options.onDrop(undefined, undefined, undefined, main3DNode);
        }
        avp.analytics.track('viewer.view_in_3d');
    }

    // If another object was selected while waiting for the model to load, stop other selections.
    _onAggregatedSelectionChanged(selection) {
        const selected = selection.selections && selection.selections.length === 1 && selection.selections[0];
        const dbId = selected.dbIdArray && selected.dbIdArray.length === 1 && selected.dbIdArray[0];
        if (dbId === this.selectedObjectID || !selection.selections.length) {
            return;
        }

        this.hidingObjectInterval && clearInterval(this.hidingObjectInterval);
        this.viewer.removeEventListener(av.GEOMETRY_LOADED_EVENT, this._onGeometryLoaded);
        this.viewer.removeEventListener(av.AGGREGATE_SELECTION_CHANGED_EVENT, this._onAggregatedSelectionChanged);
        this.chosenPos = null;
        this.selectedObjectID = null;
    }

    _onModelAdded({ model }) {
        this.hidingObjectInterval && clearInterval(this.hidingObjectInterval);

        if (!this.selectedObjectID || !model.is3d()) {
            return;
        }

        this._waitForAllModels().then(() => this._placeMeToObject());
    }

    _onModelUnloaded() {
        this.hidingObjectInterval && clearInterval(this.hidingObjectInterval);
    }

    async _placeMeToObject() {

        this.isGeometryLoaded = this._loadingAllGeometriesDone();

        // Get the objects bounding boxes
        const boxes = this._getAllBoundingBoxes();
        this.selectionBox = boxes[this.selectedObjectID];

        if (!this.selectionBox) {
            // In case the selected object doesn't have a bounding box, locate the camera
            // based on the 2D position and size of the selected object.
            if (this.pos3dFrom2d) {
                // look forward along the y axis
                let target = this.pos3dFrom2d.clone().add(new THREE.Vector3(0, 1, 0));

                const dropMeTool = this.dropMeExt.dropMeTool;
                const bubbleNode = this.viewer.model.getDocumentNode(); // Assume only a single 3D model when using "view in 3D".

                this.pos3dFrom2d = await dropMeTool._applyModelToViewerTransform(this.pos3dFrom2d, bubbleNode);
                target = await dropMeTool._applyModelToViewerTransform(target, bubbleNode);

                if (this.options.onHandleViewIn3D) {
                    this.options.onHandleViewIn3D(this.pos3dFrom2d, target, false);
                } else {
                    console.warn('Legacy event. Do not use. Please use onHandleViewIn3D CB instead.');

                    this.dropMeExt.dispatchEvent({
                        type: DropMeExtension.POSITION_FOUND_EVENT,
                        position: this.pos3dFrom2d,
                        target,
                        isPosFrom2dTo3dMatrix: true
                    });
                }
            }
            return;
        } else {
            this._setBoundingBoxPoints();

            // Separate the selected object's bounding box
            this.selectionCenter = this.selectionBox.getCenter(new THREE.Vector3());
            delete boxes[this.selectedObjectID];
        }

        // Create 8 optional positions around the selected target
        const optionalPositionsObjects = this._createCandidates();

        // Create a list of relatively close objects, so we don't check intersections with
        // un-relevant objects and decrease performance.
        const closeBoxesKeys = this._getCloseBoxesKeys(boxes);

        // Choose the position options which have the least interferences
        const betterOptions = this._getBetterCandidates(optionalPositionsObjects, boxes, closeBoxesKeys);

        // Choose the best option from the betterOptions
        this.chosenPos = this._chooseBestCandidate(betterOptions, boxes, closeBoxesKeys);

        // console.log('Chosen Position >> ', this.chosenPos.index, this.chosenPos);

        const applySelectionCB = () => this._applySelections(this.chosenPos);

        // Notify about the chosen position
        if (this.options.onHandleViewIn3D) {
            this.options.onHandleViewIn3D(this.chosenPos.position, this.selectionCenter, true, applySelectionCB);
        } else {
            console.warn('Legacy event. Do not use. Please use onHandleViewIn3D CB instead.');

            this.dropMeExt.dispatchEvent({
                type: DropMeExtension.POSITION_FOUND_EVENT,
                position: this.chosenPos.position,
                target: this.selectionCenter,
                applySelectionCB
            });
        }

        if (!this.isGeometryLoaded){
            this._waitForAllInstanceTrees().then(() => {
                this.hidingObjectInterval && clearInterval(this.hidingObjectInterval);
                this.hidingObjectInterval = setInterval(() => this._hideCurrentIntersections(), ViewObjectIn3D.HIDING_INTERVAL_FREQUENCY);
            });
        }
    }

    _getAllBoundingBoxes() {
        let boxes = {};
        const models = this.viewer.getVisibleModels();
        for (let i = 0; i < models.length; i++) {
            const fragmentList = models[i].getFragmentList();
            boxes = Object.assign(boxes, this._getBoundingBoxes(fragmentList));
        }
        return boxes;
    }

    _getBoundingBoxes(fragmentList) {
        const boxes = {};
        const tmpBox = new THREE.Box3();
        for (let fragId = 0; fragId < fragmentList.fragments.length; fragId++) {
            const dbid = fragmentList.fragments.fragId2dbId[fragId];
            if (!boxes[dbid]) {
                boxes[dbid] = new THREE.Box3();
            }
            fragmentList.getWorldBounds(fragId, tmpBox);
            boxes[dbid].union(tmpBox);
        }

        return boxes;
    }

    _createCandidates() {

        const height = this._getHeight(this.selectionCenter.z);
        this.maxDistance = Number.MIN_VALUE;

        // Checks if the object has a very wide side relatively to it's other side so we can ignore
        // angles that look at it from the narrower side.
        const rightVec = this.viewer.navigation.getWorldRightVector();
        const upVec = this.viewer.navigation.getWorldUpVector();
        const frontVec = upVec.cross(rightVec);
        const size = this.selectionBox.getSize(new THREE.Vector3());

        const rightSide = Math.abs(size.clone().multiply(rightVec).length());
        const frontSide = Math.abs(size.clone().multiply(frontVec).length());

        let widerVector;
        const wideSide = Math.max(rightSide , frontSide);
        const narrowSide = Math.min(rightSide , frontSide);
        if (wideSide / narrowSide > ViewObjectIn3D.COVERAGE_FACTOR)
            widerVector = wideSide === rightSide ? rightVec : frontVec;

        // Set the camera to be Perspective before calling this.viewer.navigation.computeFit
        // so we will get the right positions for bimwalk.
        this.viewer.navigation.toPerspective();

        const options = [];
        const rayTarget = new THREE.Vector3();
        for (let index = 0; index < ViewObjectIn3D.NUMBER_OF_POSITION; ++index) {
            const angle = (index / (ViewObjectIn3D.NUMBER_OF_POSITION / 2)) * Math.PI;

            // get a positon that fits the the selected object to view
            const newPos = this.selectionCenter.clone().add(new THREE.Vector3(Math.cos(angle), -Math.sin(angle), 0));
            const fov = this.viewer.navigation.getVerticalFov();
            const camera = this.viewer.getCamera();
            const aspect = camera.aspect;
            const optionalPositionObject = this.viewer.navigation.computeFit(newPos, this.selectionCenter, fov , this.selectionBox, aspect);
            const { position } = optionalPositionObject;

            // get the camera a bit further from the object
            const direction = position.clone().sub(this.selectionCenter);
            position.add(direction.clone().multiplyScalar(0.1));

            // set the height
            position.z = height;

            // If the wider side dot eye vector is greater then 0.8 we would like to ignore this position.
            let dot = widerVector && direction.normalize().dot(widerVector);
            let ignore = dot && Math.abs(dot) > Math.cos(THREE.Math.degToRad(ViewObjectIn3D.IGNORE_ANGLE));

            // create a ray from the optional position towards the selected target and compute distances
            const newDiff = this.selectionCenter.clone().sub(position.clone());
            const dir = newDiff.clone().normalize();
            const ray = new THREE.Ray(position.clone(), dir.clone());
            const intersectionPos = ray.intersectBox(this.selectionBox, rayTarget);
            const distanceToTarget = intersectionPos && intersectionPos.sub(position).length();
            const distanceToCenterTarget = newDiff.length();

            // this.maxDistance is used to remove un-relevant objects from calculations to improve performance
            this.maxDistance = Math.max(this.maxDistance, distanceToTarget, distanceToCenterTarget);

            // When the bounding box's size is 0 in one or more dimensions, the ray can miss the object, and in that case we eliminate this option.
            distanceToTarget && !ignore && options.push({
                index,
                position,
                ray,
                distanceToTarget,
                distanceToCenterTarget,
                selectionID: this.selectedObjectID,
                intersections: [],
                cornerIntersections: [],
                cornerIntersectionsToHide: [],
            });
        }

        return options;
    }

    _getClientViewBoundingBox(camera) {
        const clientBB = new THREE.Box3();
        for (let k = 0; k < this.boundingBoxPoints.length; k++) {
            const vec = this.boundingBoxPoints[k];
            const point = this.viewer.impl.worldToClient(vec, camera);
            if (clientBB.min.x > point.x) clientBB.min.x = point.x;
            if (clientBB.max.x < point.x) clientBB.max.x = point.x;
            if (clientBB.min.y > point.y) clientBB.min.y = point.y;
            if (clientBB.max.y < point.y) clientBB.max.y = point.y;
        }
        return clientBB;
    }

    _setBoundingBoxPoints() {
        this.boundingBoxPoints = [];
        for (let k = 0; k < 8; k++) {
            const vec = new THREE.Vector3();
            vec.x = !!(k & 0x1) === false ? this.selectionBox.max.x : this.selectionBox.min.x;
            vec.y = !!(k & 0x2) === false ? this.selectionBox.max.y : this.selectionBox.min.y;
            vec.z = !!(k & 0x4) === false ? this.selectionBox.max.z : this.selectionBox.min.z;
            this.boundingBoxPoints.push(vec);
        }
    }

    _getCloseBoxesKeys(boxes) {
        const tmpCenter = new THREE.Vector3();
        return Object.keys(boxes).filter(key => {
            const center = boxes[key].getCenter(tmpCenter);
            const diff = center.clone().sub(this.selectionCenter);
            const dist = diff.length();
            return dist < this.maxDistance * ViewObjectIn3D.DISTANCE_FACTOR;
        });
    }

    // Choose the position options which have the least interferences
    _getBetterCandidates(optionalPositionsObjects, boxes, closeBoxesKeys) {

        let betterOptions = [];
        let minIntersects = Number.POSITIVE_INFINITY;

        for (let i = 0; i < optionalPositionsObjects.length; ++i) {
            const posObj = optionalPositionsObjects[i];

            // This check is done on the object's bounding boxes and is used in cases when the geometry is not loaded yet.
            if (!this.isGeometryLoaded) {
                // Intersections between the camera's position and the object's bounding box
                posObj.intersections = this._getIntersectionsOfRayWithBoundingBoxes(posObj, posObj.ray, minIntersects, boxes, closeBoxesKeys);
            } else {
                const candidates = [];
                posObj.intersections = [];
                this.viewer.impl.rayIntersect(posObj.ray, false, false, false, candidates);
                let distToObject = this._getClosestIntersectionWithSelectedObject(candidates);
                if (!distToObject) {
                    // If no intersection with object, use the distance to it's center.
                    distToObject = posObj.distanceToCenterTarget;
                } else {
                    // If there is an intersection with object - use it, instead of the not-always-accurate the distance to the bounding-box.
                    posObj.distanceToTarget = distToObject;
                }

                // Consider only intersections that are closer than the selected object's intersection.
                candidates.forEach(item => {
                    if (item.dbId !== this.selectedObjectID && posObj.intersections.indexOf(item.dbId) === -1 && item.distance < distToObject) {
                        posObj.intersections.push(item.dbId);
                    }
                });
            }

            // Make a list of the optional positions that have the least objects interfering
            if (posObj.intersections.length === minIntersects) {
                betterOptions.push(posObj);
            } else if (posObj.intersections.length < minIntersects) {
                betterOptions = [posObj];
                minIntersects = posObj.intersections.length;
            }
        }

        return betterOptions;
    }

     // Choose the best option from the betterOptions
    _chooseBestCandidate(betterOptions, boxes, closeBoxesKeys) {

        let chosenPos;
        let minIntersections = Number.MAX_VALUE;
        betterOptions.forEach(posObj => {

            chosenPos = chosenPos || posObj;

            // Create rays from the optional position to 8 corenrs of the selected object's bounding box
            // and store the intersections
            for (let k = 0; k < this.boundingBoxPoints.length; k++) {
                const vec = this.boundingBoxPoints[k];

                // Choose a point that is slightly towards the center of the object
                const offset = vec.clone().sub(this.selectionCenter).multiplyScalar(0.1);
                vec.sub(offset);

                // To Do: If geometry is already loaded: find a better way to choose more points on the object.

                const dir = vec.clone().sub(posObj.position).normalize();
                const ray = new THREE.Ray(posObj.position.clone(), dir);

                let cornerIntersections = [];

                if (!this.isGeometryLoaded) {
                    cornerIntersections = this._getIntersectionsOfRayWithBoundingBoxes(posObj, ray, minIntersections, boxes, closeBoxesKeys);
                } else {
                    this.viewer.impl.rayIntersect(ray, false, false, false, cornerIntersections);
                    const distToObject = this._getClosestIntersectionWithSelectedObject(cornerIntersections);
                    // If no intersection with the selected object, don't check intersections with other objects for this corner.
                    if (!distToObject) {
                        continue;
                    }

                    // Consider only objects that are closer then the selected object's intersection.
                    cornerIntersections = cornerIntersections.filter(intersection => intersection.dbId !== this.selectedObjectID && intersection.distance <= distToObject);
                    cornerIntersections = cornerIntersections.map(intersection => intersection.dbId);
                }

                // This list is made for help choosing the right position.
                // Make the array of corner intersections to be of unique ones.
                if (cornerIntersections.length) {
                    posObj.cornerIntersections.push(...cornerIntersections);
                    posObj.cornerIntersections = [...new Set(posObj.cornerIntersections)];
                    // No need to continiune checking in case there are more intersections then the minimum (of the current chosen one).
                    if (posObj.cornerIntersections.length > minIntersections) break;
                }

                // This is a list of objects to hide in case this optional position will be chosen.
                // We would like to hide only objects that were intersected with the front parts of the bounding box.
                if (this.isGeometryLoaded) {
                    const vecDir = vec.clone().sub(this.selectionCenter);
                    const eyeDot = vecDir.dot(posObj.ray.direction);
                    if (eyeDot > 0 && cornerIntersections.length) {
                        posObj.cornerIntersectionsToHide.push(...cornerIntersections);
                        posObj.cornerIntersectionsToHide = [...new Set(posObj.cornerIntersections)];
                    }
                }
            }

            let count = posObj.cornerIntersections.length;
            if (this._isInsideOtherObject(posObj,  boxes, closeBoxesKeys)) {
                count++;
            }

            // Choose the option with the least corner intersections.
            // If it is the same, choose the one that is closest to the center of the object.
            // If it is the same, choose the one that is closer to intersection with the object / it's boundng-box.
            if (count < minIntersections) {
                minIntersections = count;
                chosenPos = posObj;
            } else if (count === minIntersections) {
                if (posObj.distanceToCenterTarget === chosenPos.distanceToCenterTarget) {
                    if (posObj.distanceToTarget < chosenPos.distanceToTarget) {
                        chosenPos = posObj;
                    }
                } else if (posObj.distanceToCenterTarget < chosenPos.distanceToCenterTarget) {
                    chosenPos = posObj;
                }
            }
        });

        return chosenPos;
    }

    _getIntersectionsOfRayWithBoundingBoxes(candidate, ray, minimumIntersections, boxes, keys) {
        const intersections = [];
        const l = keys.length;
        const rayTarget = new THREE.Vector3();

        for (let i = 0; i < l; ++i)
        {
            const dbid = keys[i];
            const box = boxes[dbid];
            const parsedDbId = parseInt(dbid);
            const vec = box && ray.intersectBox(box, rayTarget);
            const distToBox = vec && candidate.position.clone().sub(vec).length();

            if (distToBox) {
                if (distToBox < candidate.distanceToTarget) {
                    intersections.push(parsedDbId);
                    if (intersections.length > minimumIntersections) break;
                }
            }
        }

        return intersections;
    }

    _isInsideOtherObject(candidate, boxes, dbids) {
        for (let i = 0; i < dbids.length; ++i)
        {
            const dbid = dbids[i];
            const box = boxes[dbid];
            const isContain = box.containsPoint(candidate.position);
            if (isContain)
                return true;
        }

        return false;
    }

    _getClosestIntersectionWithSelectedObject(intersections) {
        const selectionDist = intersections.filter(intersection => intersection.dbId === this.selectedObjectID);
        let min = Number.MAX_VALUE;
        if (!selectionDist[0]) {
            return null;
        }
        for (let j = 0; j < selectionDist.length; j++) {
            if (selectionDist[j].distance < min) {
                min = selectionDist[j].distance;
            }
        }
        return min;
    }

    _hideCurrentIntersections(withCorners) {

        if (!this.chosenPos) {
            return;
        }

        let needsUpdate = false;
        const candidates = [];
        this.viewer.impl.rayIntersect(this.chosenPos.ray, false, false, false, candidates);
        needsUpdate = this._hideIntersectionsCandidates(candidates, true);

        // When geometry finnished loading we will check intersections towards the corners of object
        if (withCorners) {
            needsUpdate = this._hideCornersIntersections() || needsUpdate;
        }

        if (needsUpdate) {
            this.viewer.impl.invalidate(true, true, true);
        }
    }

    _hideCornersIntersections() {
        let needsUpdate = false;
        for (let k = 0; k < this.boundingBoxPoints.length; k++) {
            const vec = this.boundingBoxPoints[k];
            for (let i = 0; i < ViewObjectIn3D.NUMBER_OF_CORNER_INTERSECTIONS; i++) {
                const cornerCandidates = [];
                const offset = vec.clone().sub(this.selectionCenter).multiplyScalar(i / ViewObjectIn3D.NUMBER_OF_CORNER_INTERSECTIONS);
                vec.sub(offset);
                const dir = vec.clone().sub(this.chosenPos.position).normalize();
                const ray = new THREE.Ray(this.chosenPos.position.clone(), dir);
                this.viewer.impl.rayIntersect(ray, false, false, false, cornerCandidates);

                needsUpdate = this._hideIntersectionsCandidates(cornerCandidates) || needsUpdate;
            }
        }

        return needsUpdate;
    }

    _hideIntersectionsCandidates(candidates, isCenterIntersection) {
        const length = candidates.length;
        let needsUpdate = false;
        let distToObject;
        if (length > 1) {
            distToObject = this._getClosestIntersectionWithSelectedObject(candidates);
            if (isCenterIntersection && distToObject && !this._isSelected(this.selectedObjectID) && this.allTreesLoaded()) {
                this.viewer.select(this.selectedObjectID);
            }
            if (isCenterIntersection || distToObject) {
                for (let i = 0; i < length; i++) {
                    const item = candidates[i];
                    if (item.dbId !== this.selectedObjectID && item.distance < (distToObject || this.chosenPos.distanceToCenterTarget)) {
                        this.viewer.hide(item.dbId);
                        needsUpdate = true;
                    }
                }
            }
        }
        return needsUpdate;
    }

    _isSelected(dbId) {
        const models = this.viewer.getVisibleModels();
        for(let i = 0; i < models.length; ++i) {
            if (models[i].selector.isSelected(dbId)) {
                return true;
            }
        }
        return false;
    }

    // Select the selected object and hide the covering objects
    _applySelections(chosenPos) {

        if (!chosenPos || !this.allTreesLoaded()) {
            return;
        }

        if(!this._isSelected(chosenPos.selectionID)) {
            this.viewer.select(chosenPos.selectionID);
        }

        if (this.isGeometryLoaded) {
            this._hideCurrentIntersections(true);
        } else {

            for (let i = 0; i < chosenPos.intersections.length; ++i) {
                this.viewer.hide(chosenPos.intersections[i]);
            }

            for (let i = 0; i < chosenPos.cornerIntersectionsToHide.length; ++i) {
                this.viewer.hide(chosenPos.cornerIntersectionsToHide[i]);
            }
        }
    }

    // get camera's position height from level's height + avatar's height
    _getHeight(elevation) {
        const units = this.viewer.model.getData().metadata['distance unit'].value || 'foot';
        const unitScale = Autodesk.Viewing.Private.convertUnits(units, 'm', 1, 1);
        const avatarHeight = 1.8 / unitScale; // this.bimwalkExt.tool.navigator.get('cameraDistanceFromFloor')

        const aec = this.viewer.model.getDocumentNode().getAecModelData();
        const placementTf = this.viewer.model.myData.placementWithOffset;

        const floors = aecModelDataToLevels(aec, placementTf);
        if (!floors.length) {
            return this.selectionBox.min.z + avatarHeight;
        }

        let level;
        if (elevation < floors[0].zMin) {
            level = floors[0];
        }
        else if (elevation > floors[floors.length - 1].zMax) {
            level = floors[floors.length - 1];
        }
        else {
            level = floors.find(f => f.zMin <= elevation && f.zMax >= elevation);
        }

        return level.zMin + avatarHeight;
    }

    _pending3DLoaders() {
        return !!this.viewer.impl.loaders.filter(loader => loader.is3d()).length;
    }

    _loadingAllGeometriesDone() {
        const models = this.viewer.getVisibleModels();
        for(let i = 0; i < models.length; ++i) {
            const loaded = models[i].isLoadDone();
            if (!loaded) {
                return false;
            }
        }
        return !this._pending3DLoaders();
    }

    _waitForAllModels() {

        return new Promise((resolve) => {

            const allRootModelLoaded = () => {
                const models = this.viewer.getVisibleModels();
                for(let i = 0; i < models.length; ++i) {
                    if (!models[i].modelRootLoaded) {
                        return false;
                    }
                }
                return !this._pending3DLoaders();
            };

            const cb = () => {
                if (allRootModelLoaded()) {
                    resolve();
                } else {
                    this.viewer.addEventListener(av.MODEL_ROOT_LOADED_EVENT, cb, { once: true });
                }
            };

            cb();
        });
    }

    allTreesLoaded() {
        const models = this.viewer.getVisibleModels();
        for(let i = 0; i < models.length; ++i) {
            if (!models[i].getObjectTree()) {
                return false;
            }
        }
        return !this._pending3DLoaders();
    }

    _waitForAllInstanceTrees() {

        return new Promise((resolve) => {

            const cb = () => {
                if (this.allTreesLoaded()) {
                    resolve();
                } else {
                    this.viewer.addEventListener(av.OBJECT_TREE_CREATED_EVENT, cb,  { once: true });
                }
            };

            cb();
        });
    }

    _onGeometryLoaded() {

        if (!this._loadingAllGeometriesDone()) {
            return;
        }

        this.viewer.removeEventListener(av.GEOMETRY_LOADED_EVENT, this._onGeometryLoaded);
        this.hidingObjectInterval && clearInterval(this.hidingObjectInterval);

        if (!this.selectedObjectID || !this.chosenPos) {
            return;
        }

        // In case gemotry was loaded before the instance tree, it will not be possible to select the object.
        // We will wait until the instance tree is loaded in order to select the object.
        this._waitForAllInstanceTrees().then(() => {

            // Select the right object again (clearSelection is called onGeometryLoaded)
            this.viewer.clearSelection();
            if(!this._isSelected(this.selectedObjectID)) {
                this.viewer.select(this.selectedObjectID);
            }

            // Now that the geometey is loaded we can check with a raycast if objects have to be hidden
            this._hideCurrentIntersections(true);

            this.viewer.impl.invalidate(true, true, true);

            this.chosenPos = null;
            this.selectedObjectID = null;
        });
    }

    // In cases the object is does not exist in the 3d view, we will locate the camera on the coordinations
    // taken based on the 2d position of the selected object.
    _getSelected3dPosFrom2d(selectedID, model) {
        const bounds = this.viewer.impl.computeSelectionBounds([selectedID], model);
        const point = this.viewer.impl.worldToClient(bounds.getCenter(new THREE.Vector3()));
        const obj = this.dropMeExt.dropMeTool._get3DCameraPositionAndVp(point.x, point.y);
        return obj && obj.pos;
    }
}

// The frequency of checking objects to hide.
ViewObjectIn3D.HIDING_INTERVAL_FREQUENCY = 300;

// When geometry is already loaded we want to check intersections so we can hide them.
// In order to hide as much covering objects as possible we take the 8 bounding box's corners and
// raycast towards NUMBER_OF_CORNER_INTERSECTIONS points on the way between the center and the corners
ViewObjectIn3D.NUMBER_OF_CORNER_INTERSECTIONS = 10;

// In order to reduce calculations of rey-box intersections we remove from the iteration boxes that are far away
// from the chosen object (more than DISTANCE_FACTOR times the the distance between the camera and the object)
ViewObjectIn3D.DISTANCE_FACTOR = 3;

// In case of narrow objects like doors and walls, when one side is wider then the other by more then COVERAGE_FACTOR times
//  we would like to ignore positions that looks at the object from its narrow side.
ViewObjectIn3D.COVERAGE_FACTOR = 4.0;

// When the angle between the camera's direction and to wider side of the object is less hen IGNORE_ANGLE dgrees
// Ignore this position
ViewObjectIn3D.IGNORE_ANGLE = 35;

// The number of position candidates we create around the chosen object.
ViewObjectIn3D.NUMBER_OF_POSITION = 8;
